import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ShieldCheck, 
  UserCircle,
  Menu,
  X,
  LogOut,
  Settings
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CompactHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { icon: <LayoutDashboard className="w-4 h-4" />, label: 'Painel', path: '/dashboard' },
    ...(isAdmin ? [{ icon: <ShieldCheck className="w-4 h-4" />, label: 'Admin', path: user?.role === 'superadmin' ? '/admin' : '/admin-empresa' }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-black/80 backdrop-blur-lg border-b border-white/10">
      <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          
          {/* Logo & Desktop Nav */}
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="w-6 h-6 text-orange-500" />
              <span className="text-lg font-bold text-white hidden sm:block">Dashboard</span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {menuItems.map((item, index) => (
                <Link key={index} to={item.path}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`text-gray-300 hover:text-white hover:bg-white/10 ${
                      location.pathname === item.path ? 'bg-white/10 text-white' : ''
                    }`}
                  >
                    {item.icon}
                    <span className="ml-2">{item.label}</span>
                  </Button>
                </Link>
              ))}
            </nav>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            <Link to="/settings">
              <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white hover:bg-white/10" title="Configurações">
                <Settings className="w-4 h-4" />
              </Button>
            </Link>

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 p-0 hover:bg-transparent">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white font-semibold hover:ring-2 hover:ring-orange-400 transition-all">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-black border-white/20 text-white" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.name}</p>
                    <p className="text-xs leading-none text-gray-400">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white">
                  <UserCircle className="mr-2 h-4 w-4" />
                  <span>Minha Conta</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-400 hover:bg-red-500/10 hover:text-red-300 focus:bg-red-500/10 focus:text-red-300">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-md"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-white/10 bg-black">
          <div className="space-y-1 px-2 pb-3 pt-2">
            {menuItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block"
              >
                <Button
                  variant="ghost"
                  className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10"
                >
                  {item.icon}
                  <span className="ml-2">{item.label}</span>
                </Button>
              </Link>
            ))}
             <Link
                to="/settings"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block"
              >
                <Button
                  variant="ghost"
                  className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10"
                >
                  <Settings className="w-4 h-4" />
                  <span className="ml-2">Configurações</span>
                </Button>
              </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default CompactHeader;